// Portado da Central do Perito (`src/lib/cnj.ts`, commit f5c1923) em 03/11, só com os imports trocados (000385).
import { buscarTribunalPorCodigoCnj } from "./tribunais";

/**
 * Validação e parsing de números de processo CNJ.
 * Formato: NNNNNNN-DD.AAAA.J.TR.OOOO
 *   N = sequencial (7), D = dígito (2), A = ano (4),
 *   J = segmento (1), TR = tribunal (2), O = origem (4)
 */
export interface CnjParsed {
  sequencial: string;
  digito: string;
  ano: string;
  segmento: string; // J
  tribunal: string; // TR
  origem: string;
  valido: boolean;
  formatado: string;
}

export function aplicarMascaraCnj(valor: string): string {
  const d = valor.replace(/\D/g, "").slice(0, 20);
  let out = "";
  if (d.length > 0) out += d.slice(0, 7);
  if (d.length >= 8) out = d.slice(0, 7) + "-" + d.slice(7, 9);
  if (d.length >= 10) out += "." + d.slice(9, 13);
  if (d.length >= 14) out += "." + d.slice(13, 14);
  if (d.length >= 15) out += "." + d.slice(14, 16);
  if (d.length >= 17) out += "." + d.slice(16, 20);
  return out;
}

export function parseCnj(valor: string): CnjParsed | null {
  const d = valor.replace(/\D/g, "");
  if (d.length !== 20) return null;
  const sequencial = d.slice(0, 7);
  const digito = d.slice(7, 9);
  const ano = d.slice(9, 13);
  const segmento = d.slice(13, 14);
  const tribunal = d.slice(14, 16);
  const origem = d.slice(16, 20);

  // Validação dígito verificador (módulo 97 base 10)
  // N=7 D=2 A=4 J=1 TR=2 O=4
  // valor de entrada para mod 97: NNNNNNN AAAA J TR OOOO DD
  const num = `${sequencial}${ano}${segmento}${tribunal}${origem}${digito}`;
  let resto = 0;
  for (let i = 0; i < num.length; i++) {
    resto = (resto * 10 + Number(num[i])) % 97;
  }
  const valido = resto === 1;

  return {
    sequencial,
    digito,
    ano,
    segmento,
    tribunal,
    origem,
    valido,
    formatado: `${sequencial}-${digito}.${ano}.${segmento}.${tribunal}.${origem}`,
  };
}

export const SEGMENTO_LABEL: Record<string, string> = {
  "1": "Supremo Tribunal Federal",
  "2": "Conselho Nacional de Justiça",
  "3": "Superior Tribunal de Justiça",
  "4": "Justiça Federal",
  "5": "Justiça do Trabalho",
  "6": "Justiça Eleitoral",
  "7": "Justiça Militar da União",
  "8": "Justiça Estadual",
  "9": "Justiça Militar Estadual",
};

export interface CnjDecodificado {
  numeroFormatado: string;
  valido: boolean;
  ano: string;
  segmentoLabel?: string;
  tribunalSigla?: string;
  uf?: string;
}

/** Extrai o primeiro número CNJ encontrado em um texto livre. */
export function extrairNumeroCnj(texto: string): string | null {
  const m = texto.match(/\d{7}[-.\s]?\d{2}[.\s]?\d{4}[.\s]?\d[.\s]?\d{2}[.\s]?\d{4}/);
  if (m) return aplicarMascaraCnj(m[0]);
  const digits = texto.replace(/\D/g, "");
  if (digits.length === 20) return aplicarMascaraCnj(digits);
  return null;
}

/** Decodifica tribunal, segmento, ano e UF a partir do próprio número CNJ. */
export function decodificarCnj(valor: string): CnjDecodificado | null {
  const parsed = parseCnj(valor);
  if (!parsed) return null;
  const trib = buscarTribunalPorCodigoCnj(parsed.segmento, parsed.tribunal);
  return {
    numeroFormatado: parsed.formatado,
    valido: parsed.valido,
    ano: parsed.ano,
    segmentoLabel: SEGMENTO_LABEL[parsed.segmento],
    tribunalSigla: trib?.sigla,
    uf: trib?.uf,
  };
}
